/**
 * Collection URL and Redux table identity helpers (scoped slug: personal vs org).
 */

import { isOrganizationScopingDisabled } from './organizationScoping';

export function normalizeOrgSegment(org: string | null | undefined): string {
  if (org == null || typeof org !== 'string') return '';
  return org
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '');
}

export function buildCollectionTableKey(slug: string | undefined | null, organization?: string | null): string {
  const s = typeof slug === 'string' ? slug.replace(/[!]+$/, '').trim() : '';
  const o = isOrganizationScopingDisabled() ? '' : normalizeOrgSegment(organization || '');
  return o ? `${o}/${s}` : s;
}

/** Path for router: /:slug or /:org/:slug (no /component prefix). */
export function buildCollectionPath(meta: { slug?: string; organization?: string | null }): string {
  const s = typeof meta.slug === 'string' ? meta.slug.replace(/[!]+$/, '').trim() : '';
  const o = isOrganizationScopingDisabled() ? '' : normalizeOrgSegment(meta.organization || '');
  if (!s) return '/';
  return o ? `/${o}/${s}` : `/${s}`;
}

/** First path segment must not be a known app route for `/:org/:slug` collection URLs. */
const TOP_SEGMENT_APP_ROUTES = new Set([
  'login',
  'ticket',
  'forms',
  'public',
  'my-submissions',
  'info',
  'etsg-privacy',
  'privacy',
  'profile',
  'registration',
  'register',
  'collections',
  'files',
  'admin',
  'actions',
  'inbox',
  'state',
  'components',
]);

/**
 * If the pathname is a collection table route, returns slug, optional org segment, and Redux table key.
 * Supports legacy `/component/:slug`.
 */
export function parseCollectionTablePath(pathname: string): {
  slug: string;
  org: string | null;
  tableKey: string;
} | null {
  const parts = pathname.split('/').filter(Boolean);
  if (parts.length === 2 && parts[0].toLowerCase() === 'component') {
    const slug = parts[1].replace(/[!]+$/, '').trim();
    if (!slug) return null;
    return { slug, org: null, tableKey: buildCollectionTableKey(slug, null) };
  }
  if (parts.length === 1) {
    const seg = parts[0].replace(/[!]+$/, '').trim();
    if (!seg || TOP_SEGMENT_APP_ROUTES.has(seg.toLowerCase())) return null;
    return { slug: seg, org: null, tableKey: buildCollectionTableKey(seg, null) };
  }
  if (parts.length === 2) {
    const org = parts[0].replace(/[!]+$/, '').trim();
    const slug = parts[1].replace(/[!]+$/, '').trim();
    if (!org || !slug || TOP_SEGMENT_APP_ROUTES.has(org.toLowerCase())) return null;
    return { slug, org, tableKey: buildCollectionTableKey(slug, org) };
  }
  return null;
}
